import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { __ } from '@wordpress/i18n';

// components
import Button from "../../../common/components/inputs/Button";
import Dropdown from "../../../common/components/inputs/Dropdown";
import TextField from "../../../common/components/inputs/TextField";
import Title from "../../../common/components/Title";
import SubTitle from "../../../common/components/SubTitle";
import Table from "./components/Table";
import Pagination from "../../../common/components/Pagination";

const Brands = ({page}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [brands, setBrands] = useState([]);
    const [flatBrands, setFlatBrands] = useState([]);
    const [brandsData, setBrandsData] = useState({ brands: [{ brand_name: '', brand_slug: '', parent: 0 }] });
    const [editingBrand, setEditingBrand] = useState(null);
    const [selectParent, setSelectParent] = useState({ label: 'None', value: 0 }); 
    const [totalPage, setTotalPage] = useState(1);
    const [postPerPage, setPostPerPage] = useState(10);

    useEffect(() => {
        fetchData();
    }, []);

    const generateSlug = (text) => {
        return text
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '');
    };

    const flattenBrands = (brands, parentName = 'None', parentId = 0) => {
        let flatList = [];

        brands.forEach((brand) => {
            flatList.push({
                id: brand.id,
                name: brand.name,
                slug: brand.slug,
                parentName,
                parent: parentId,
            });

            if (brand.children && brand.children.length > 0) {
                flatList = flatList.concat(flattenBrands(brand.children, brand.name, brand.id));
            }
        });

        return flatList;
    };

    const fetchData = async () => {
        setIsLoading(true); 
        try {
            const response = await fetch(`${EASYCOMMERCE.rest_base}/products/brands?page=${page}&per_page=${postPerPage}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': EASYCOMMERCE.nonce,
                },
            });
            const data = await response.json();
            setBrands(data.data.brands);
            setTotalPage(data.data.pagination.total_pages);

            const flattened = flattenBrands(data.data.brands);
            setFlatBrands(flattened);

        } catch (error) {
            toast.error('Failed to fetch brands.');
        }finally {
            setIsLoading(false); 
        }  
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'brand_name') {
            const newSlug = generateSlug(value);
            setBrandsData((prev) => ({
                ...prev,
                brands: [{ ...prev.brands[0], brand_name: value, brand_slug: newSlug, parent: prev.brands[0].parent }]
            }));
        }
    };

    const handleParentChange = (option) => {
        setBrandsData((prev) => ({
            ...prev,
            brands: [{ ...prev.brands[0], parent: option.value }]
        }));
        setSelectParent(option);
    };

    const handleEditBrand = (id) => {
        easycommerce_modal(true);
    
        const brand  = findBrandById(brands, id);
        const flatEntry = flatBrands.find(fc => fc.id === id);
    
        if (brand && flatEntry) {
            setBrandsData({
                brands: [{
                    brand_name: brand.name,
                    brand_slug: brand.slug,
                    parent: flatEntry.parent 
                }]
            });
            if (flatEntry.parent && flatEntry.parent !== 0) {
                const parentBrand = flatBrands.find(fc => fc.id === flatEntry.parent);
                if (parentBrand) {
                    setSelectParent({ label: parentBrand.name, value: parentBrand.id });
                } else {
                    setSelectParent({ label: 'None', value: 0 });
                }
            } else {
                setSelectParent({ label: 'None', value: 0 });
            }
    
            setEditingBrand(id);
        }
        easycommerce_modal(false);
    };
    
    const findBrandById = (brands, id) => {
        for (let brand of brands) {
            if (brand.id === id) return brand;
            if (brand.children && brand.children.length > 0) {
                const found = findBrandById(brand.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const handleSubmit = async () => {
        const payload = {
            name: brandsData.brands[0].brand_name,
            slug: brandsData.brands[0].brand_slug,
            parent: brandsData.brands[0].parent
        };

        if (!payload.name.trim() || !payload.slug.trim()) {
            toast.error('All fields are required!');
            return;
        }
        const method = editingBrand ? 'PUT' : 'POST';

        try {
            const url = editingBrand
                ? `${EASYCOMMERCE.rest_base}/products/brands/${editingBrand}`
                : `${EASYCOMMERCE.rest_base}/products/brands`;

            easycommerce_modal(true);
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': EASYCOMMERCE.nonce,
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                toast.success(`Brand ${editingBrand ? 'updated' : 'created'} successfully`);
                fetchData();
                setBrandsData({ brands: [{ brand_name: '', brand_slug: '', parent: 0 }] });
                setEditingBrand(null);
                setSelectParent({ label: 'None', value: 0 });
                easycommerce_modal(false);
            } else {
                toast.error(data.data.message || 'Operation failed');
                easycommerce_modal(false);
            }
        } catch (error) {
            toast.error('Operation failed');
        }
    };

    const findAllDescendantIds = (brands, id) => {
        let descendantIds = [];
    
        brands.forEach(brand => {
            if (brand.parent === id) {
                descendantIds.push(brand.id);
                descendantIds = descendantIds.concat(findAllDescendantIds(brands, brand.id));
            }
        });
    
        return descendantIds;
    };
    

    const descendantIds = editingBrand ? findAllDescendantIds(flatBrands, editingBrand) : [];

    const selectedParentOptions = [
        { label: 'None', value: 0 },
        ...flatBrands
            .filter(cat => cat.id !== editingBrand && !descendantIds.includes(cat.id))
            .map(cat => ({ label: cat.name, value: cat.id }))
    ];

    const handleCancel = () => {
        easycommerce_modal(true);
 
        setBrandsData({ brands: [{ brand_name: '', brand_slug: '', parent: 0 }] });
        setEditingBrand(null);
        setSelectParent({ label: 'None', value: 0 });
     
        setTimeout(() => {
            easycommerce_modal(false);
        }, 100); 
    };

    return (
        <>
            <div className="product-panel-title mb-4">
                <h3>Brands</h3>
            </div>
            <div className="grid min-[1400px]:grid-cols-10 min-[1280px]:grid-cols-12 gap-6 items-start">
                <div className="bg-white min-[1400px]:col-span-6 min-[1280px]:col-span-12 w-full self-start border border-solid border-ec-table-stock rounded-xl">
                    <div className="flex items-center border-b pl-6 py-3.5">
                        <Title title="Brands List" />
                    </div>

                    <div className="p-6">
                        <Table
                            brands={flatBrands}
                            fetchData={fetchData}
                            onEdit={handleEditBrand}
                            isLoading={isLoading}
                        />
                    </div>
                    {totalPage > 1 && (
                        <Pagination
                            baseSlug="brands"
                            current={page}
                            total={totalPage}
                        />
                    )}
                </div>

                {/* Form Section */}
                <div className="bg-white min-[1400px]:col-span-4 min-[1280px]:col-span-12 w-full self-start border border-solid border-ec-table-stock rounded-xl">
                    <div className="flex items-center border-b pl-6 py-3.5">
                        <Title title={ editingBrand ? 'Edit Brand: ' + brandsData.brands[0].brand_name : "Add New Brand"} />
                    </div>

                    <div className="p-6 flex flex-col gap-4">
                        <div className='flex flex-col gap-2'>
                            <SubTitle SubTitle="Name" notice="It'll be used to filter products" />
                            <TextField 
                                name="brand_name" 
                                value={brandsData.brands[0].brand_name} 
                                onChange={handleChange} 
                                placeholder="Enter Name" 
                                className="h-ec-input"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <SubTitle SubTitle="Slug" notice="URL-friendly slug of the brand" />

                            <div className="h-ec-input rounded-lg font-inter text-[14px] leading-[20px] border border-ec-table-stock placeholder-ec-placeholder hover:border-ec-primary focus-within:border-ec-primary focus-within:outline-none focus-within:[box-shadow:0_0_0_4px_#F3F0FF] transition-colors duration-300 ease-in-out overflow-hidden flex ">
                                <div className="h-ec-input flex items-center justify-center text-ec-light-black pl-4">
                                    {EASYCOMMERCE.home_url}/shop/brands/
                                </div>
                                <input
                                    type="text"
                                    name="brand_slug"
                                    className="h-ec-input border-none outline-none shadow-none p-0 text-ec-body font-inter text-[14px] leading-[20px]"
                                    value={brandsData.brands[0].brand_slug}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setBrandsData(prev => ({
                                        ...prev,
                                        brands: [{ ...prev.brands[0], brand_slug: val }]
                                        }));
                                    }}
                                />
                            </div>
                        </div>


                        <div className='flex flex-col gap-2'>
                            <SubTitle SubTitle="Parent" notice="Select a parent brand if any" />
                            <div className="w-full h-ec-input">
                                <Dropdown
                                    key={editingBrand || 'new'}
                                    options={selectedParentOptions}
                                    placeholder="Select Parent"
                                    onChange={handleParentChange}
                                    value={selectParent.value}
                                />
                            </div>
                        </div>

                        <div className="flex justify-between mt-6">
                            {editingBrand ? (
                                <Button
                                    className="h-[37px] mt-[15px] easycommerce-underline-button"
                                    value="Cancel"
                                    onClick={handleCancel} 
                                />
                            ) : (
                                <div /> 
                            )}
                            <Button
                                className="easycommerce-outline-button"
                                value={editingBrand ? "Update Brand" : "Create Brand"}
                                onClick={handleSubmit}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Brands;